import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { CustomDataTable } from '../../../components/design/Datatables/CustomDataTable'
import useAuth from '../../../context/auth/useAuth'
import Loader from '../../../components/design/Loader/Loader'
import { changePaymentCard, getAllPayments, sendCardReminderEmail } from '../../../actions/payments/paymentActions'
import ManualPayment from '../../../components/Payments/ManualPayment'
import Swal from 'sweetalert2'
import ReturnPayment from '../../../components/Payments/ReturnPayment'
import paycometErrors from '../../../components/paycometError.json'

export default function AllPayments() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
	const {clientauth} = useAuth()

    const {payments, loading, retryStatus, returnStatus, pagination} = useSelector(state=>state.payment)

    const [manualPaymentSelected, setManualPaymentSelected] = useState(null)
    const [paymentToReturnSelected, setPaymentToReturnSelected] = useState(null)
    const currentYear = new Date().getFullYear()
    const availableYears = []
    for (let year = currentYear; year >= 2025; year--) {
        availableYears.push(year)
    }
    const defaultYear = availableYears.includes(currentYear) ? currentYear : availableYears[0]
    const [selectedTab, setSelectedTab] = useState(defaultYear)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [allRowsSelected, setAllRowsSelected] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')

    const loadPayments = useCallback((tabToLoad = selectedTab, pageToLoad = currentPage, limitToLoad = rowsPerPage, searchToLoad = debouncedSearch) => {
        const yearParam = tabToLoad === 'inactive' ? 'inactive' : (tabToLoad === 'all' ? 'all' : tabToLoad)
        const requestLimit = allRowsSelected ? -1 : limitToLoad
        if (clientauth) {
            dispatch(getAllPayments(yearParam, pageToLoad, requestLimit, searchToLoad))
        }
    }, [dispatch, clientauth, selectedTab, currentPage, rowsPerPage, debouncedSearch, allRowsSelected])

    const translatedPayments = payments?.map(payment => {
        const statusText =
            payment.status === 0 ? t('payments.pending') :
            payment.status === 1 ? `${t('payments.failed')} (${payment.totalRetries || 1})` :
            payment.status === 2 ? t('payments.canceled') :
            payment.status === 3 ? t('payments.paid') :
            t('payments.unknown')

        const externalPaidText = payment.externalPaid ? ` ${t('payments.externalPaid')}` : ''

        return {
            ...payment,
            translatedStatus: `${statusText}${externalPaidText}`
        }
    }) || []

    // Ya no es necesario filtrar en memoria porque el backend ya lo hace
    const tablePayments = translatedPayments
    const isInitialLoading = loading && !payments

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(searchInput.trim())
        }, 350)

        return () => clearTimeout(timeoutId)
    }, [searchInput])

    useEffect(() => {
        loadPayments(selectedTab, currentPage, rowsPerPage)
    }, [loadPayments, selectedTab, currentPage, rowsPerPage, debouncedSearch])

    useEffect(() => {
        if (retryStatus) {
            loadPayments(selectedTab)
            switch (retryStatus.message) {
                case "retryPaymentSuccess":
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'success',
                        text: t('payments.retryPaymentSuccess')
                    })
                    break;
                case "retryPaymentClientError":
                    const error = paycometErrors?.find(err => err.code === retryStatus?.typeError.toString())
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'error',
                        html: `${t('payments.retryPaymentClientError')}<br>Error: ${retryStatus.typeError}<br>${error?.description}`
                    })
                    break;
                default:
                    break;
            }
            dispatch({ type: "PAYMENT_RETRY_RESET" })
        }
    }, [retryStatus, selectedTab, dispatch, t, loadPayments])

    useEffect(() => {
        if (returnStatus) {
            loadPayments(selectedTab)
            switch (returnStatus?.message) {
                case "paymentRefundSuccess":
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'success',
                        text: t('payments.paymentRefundSuccess')
                    })
                    break;
                case "paymentRefundError":
                    const error = paycometErrors?.find(err => err.code === returnStatus?.typeError.toString())
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'error',
                        html: `${t('payments.paymentRefundError')}<br>Error: ${returnStatus.typeError}<br>${error?.description}`
                    })
                    break;
                case "quatityRequired":
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'error',
                        text: t('payments.quantityRequired')
                    })
                    break;
                case "paymentNotFound":
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'error',
                        text: t('payments.paymentNotFound')
                    })
                    break;
                default:
                    break;
            }
            dispatch({ type: "PAYMENT_RETURN_RESET" })
        }
    }, [returnStatus, selectedTab, dispatch, t, loadPayments])

    // ── Helpers de tarjeta ────────────────────────────────────────────────────
    const buildPaymentCardValue = (card) =>
        card?.payCometId && card?.payCometToken
            ? `${card.payCometId}|${card.payCometToken}`
            : ''

    const getAdminPaymentSelectedCard = (payment) => {
        const clientCards = payment?.paymentClient?.cards || []
        if (!clientCards.length) return null
        const pc = payment?.card
        if (pc?.payCometId && pc?.payCometToken) {
            const matched = clientCards.find(
                c => c.payCometId === pc.payCometId && c.payCometToken === pc.payCometToken
            )
            if (matched) return matched
        }
        return clientCards.find(c => c.favourite) || clientCards[0] || null
    }

    const handleAdminPaymentCardChange = (payment, value) => {
        const [payCometId, payCometToken] = (value || '').split('|')
        if (!payCometId) return
        const currentValue = buildPaymentCardValue(getAdminPaymentSelectedCard(payment))
        if (value !== currentValue) {
            dispatch(changePaymentCard(payment._id, payCometId, 'admin', payCometToken))
        }
    }

    const handleSendCardReminderEmail = async (payment) => {
        const result = await dispatch(sendCardReminderEmail(payment._id))
        if (result?.ok) {
            Swal.fire({
                showConfirmButton: true,
                icon: 'success',
                text: `Email enviado Correctamente${result?.data?.destinationEmail ? ` a: ${result.data.destinationEmail}` : ''}`
            })
        } else {
            Swal.fire({
                showConfirmButton: true,
                icon: 'error',
                text: `Email fallido, no se ha enviado correctamente.${result?.data?.destinationEmail ? ` Email destino: ${result.data.destinationEmail}` : ''}`
            })
        }
    }

    const formatDate = (value) => {
        if (!value) return '-'
        const date = new Date(value)
        const day = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`
        const monthNumber = date.getMonth() + 1
        const month = monthNumber > 9 ? monthNumber : `0${monthNumber}`
        return `${day}/${month}/${date.getFullYear()}`
    }

    // ─────────────────────────────────────────────────────────────────────────

    const columnsPayments = [
        {
            name: t('payments.reference'),
            width: '140px',
            minWidth: '140px',
            cell: payment => <span style={{ whiteSpace: 'nowrap' }}>{payment.paymentReference || '-'}</span>,
            selector: payment => payment.paymentReference,
            sortable: false,
        },{
            name: t('payments.paymentDate'),
            width: '120px',
            minWidth: '120px',
            cell: payment => <span style={{ whiteSpace: 'nowrap' }}>{formatDate(payment.paymentDate)}</span>,
            selector: payment => formatDate(payment.paymentDate),
            sortable: true,
        },{
            name: t('payments.quantity'),
            right: 'true',
            width: '100px',
            cell: payment =>
                payment.quantity.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2}) + "€",
            selector: (payment) => payment.quantity,
            sortable: true,
        },{
            name: t('payments.card'),
            center: 'true',
            minWidth: '180px',
            width: '180px',
            wrap: true,
            cell: payment => (payment.status === 0 || payment.status === 1) && payment.paymentReference && payment.quantity > 0 ? (
                payment?.paymentClient?.cards?.length > 0 ? (
                    <div className='d-flex justify-content-center w-100' style={{ overflow: 'hidden' }}>
                        <select
                            style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            value={buildPaymentCardValue(getAdminPaymentSelectedCard(payment))}
                            onChange={(e) => {
                                handleAdminPaymentCardChange(payment, e.target.value)
                            }}
                        >
                            {payment?.paymentClient?.cards?.map(card => (
                                <option key={card?._id} value={buildPaymentCardValue(card)}>
                                    **** {card.last4} ({card.name})
                                </option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className='d-flex justify-content-center w-100'>
                        <span>No tiene tarjeta</span>
                    </div>
                )
            ) : '',
            selector: (payment) => payment.card,
            sortable: false,
        },{
            name: t('payments.refunded'),
            right: 'true',
            width: '100px',
            hide: 'md',
            cell: payment =>
                payment.refunded ? payment.refunded.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2}) + "€" : '',
            selector: (payment) => payment.refunded ? payment.refunded.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2}) : '',
            sortable: true,
        },{
            name: t('payments.status'),
            center: 'true',
            width: '150px',
            wrap: true,
            cell: payment => payment.translatedStatus,
            selector: (payment) => payment.translatedStatus,
            sortable: true,
        },{
            name: t('payments.sepaPaid'),
            center: 'true',
            width: '90px',
            hide: 'md',
            cell: payment =>
                payment.sepaPaid ? t('global.yes') : t('global.no'),
            selector: (payment) => payment.sepaPaid ? t('global.yes') : t('global.no'),
            sortable: true,
        },{
            name: t('payments.client'),
            grow: 2,
            minWidth: '180px',
            cell: payment => {
                const client = payment.client
                    ? `${payment.client.firstname || ''} ${payment.client.lastname || ''}`
                    : null;
                const paymentClient = payment.paymentClient
                    ? `${payment.paymentClient.firstname || ''} ${payment.paymentClient.lastname || ''}`
                    : null;
                const personData = payment.ref?.personData
                    ? `${payment.ref?.personData?.firstname || ''} ${payment.ref?.personData?.lastname || ''}`
                    : null;
                const paymentPersonData = payment.paymentPersonData
                    ? `${payment.paymentPersonData.firstname || ''} ${payment.paymentPersonData.lastname || ''}`
                    : null;
                
                return (
                    <div className="d-flex flex-column">
                        {client && <>{client}<br/></>}
                        {personData && (
                            <>{personData}<br/></>
                        )}
                        {paymentClient && <small>{t('payments.paymentClient')}: {paymentClient}</small>}
                        {paymentPersonData && (
                            <>
                                <div>{paymentPersonData}</div>
                                <small>{payment.paymentPersonData.email}</small>
                            </>
                        )}
                        {!client && !personData && !paymentClient && !paymentPersonData && (
                            <span>Sin datos de cliente</span>
                        )}
                    </div>
                )
            },
            selector: payment =>
                `${payment.client?.firstname || ''} ${payment.client?.lastname || ''} ${payment.paymentClient?.firstname || ''} ${payment.paymentClient?.lastname || ''} ${payment.ref?.personData?.firstname || ''} ${payment.ref?.personData?.lastname || ''} ${payment.paymentPersonData?.firstname || ''} ${payment.paymentPersonData?.lastname || ''} ${payment.paymentPersonData?.email || ''}`,
            wrap: true,
            sortable: true,
        },{
			name: t('global.actions'),
            center: 'true',
            width:'120px',
            minWidth: '120px',
			cell: payment =>
                <div className='d-flex justify-content-center align-items-center gap-2 flex-nowrap w-100'>
                    {
                        (payment.status === 1 || payment.status === 0) &&
                            <>
                                <div onClick={() => handleSendCardReminderEmail(payment)}>
                                    <OverlayTrigger placement="top"
                                        overlay={<Tooltip id="tooltip">
                                                    Email nueva tarjeta
                                                </Tooltip>}
                                    >
                                        <i className='fas fa-credit-card cursor' aria-label='card-reminder'/>
                                    </OverlayTrigger>
                                </div>
                                <div onClick={() => setManualPaymentSelected(payment)}>
                                    <OverlayTrigger placement="top"
                                        overlay={<Tooltip id="tooltip">
                                                    {t('payments.manualPayment')}
                                                </Tooltip>}
                                    >
                                        <span className="cursor fw-bold" aria-label="manual-payment">$</span>
                                    </OverlayTrigger>
                                </div>
                            </>
                    }
                    {
                        (payment.status === 3 && (Number(payment.quantity || 0) - Number(payment.refunded || 0)) > 0 && !payment.sepaPaid) &&
                        <div onClick={() => setPaymentToReturnSelected(payment)}>
                            <OverlayTrigger placement="top"
                                overlay={<Tooltip id="tooltip">
                                            {t('payments.returnPayment')}
                                        </Tooltip>}
                            >
                                <img className="cursor" src="/images/money-return.svg" alt="eyes" height={16}/>
                            </OverlayTrigger>
                        </div>
                    }
                </div>,
			sortable: false,
		}
	]
    const ExpandedRow = ({ data }) => {
        return (
            <Container className='p-3'>
                <Row>
                    <Col xs={12} lg={6}>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('inscriptions.paymentreference')}</small> {data?.paymentReference}
                        </div>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('inscriptions.quantity')}</small> {Number(data?.quantity || 0).toFixed(2)} €
                        </div>
                        <div className='d-flex justify-content-between border-bottom p-2'>
                            <small>{t('inscriptions.paymentdate')}:</small> {data.paymentDate ? `${new Date(data.paymentDate).getDate() > 9 ? new Date(data.paymentDate).getDate() : "0" + new Date(data.paymentDate).getDate()}/${new Date(data.paymentDate).getMonth() > 8 ? parseInt(new Date(data.paymentDate).getMonth()) + 1 : "0" + (parseInt(new Date(data.paymentDate).getMonth()) + 1)}/${new Date(data.paymentDate).getFullYear() > 9 ? new Date(data.paymentDate).getFullYear() : "0" + new Date(data.paymentDate).getFullYear()}`: '-'}
                        </div>
                        <div className='d-flex justify-content-between border-bottom p-2'>
                            <small>{t('payments.refType')}:</small> {data.refType && data.ref ? t('payments.' + data.refType) : t('payments.cardRegistration')}
                        </div>
                        {data.ref
                        ?
                            <div className='d-flex justify-content-between border-bottom p-2'>
                                <small>{t('payments.club')}:</small> {data.ref.club}
                            </div>
                        :
                        <></>
                        }

                    </Col>
                    <Col xs={12} lg={6}>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('inscriptions.status')}:</small> {t('inscriptions.statusseasonpay'+data?.status)} {data.externalPaid ? ` ${t('payments.externalPaid')}` : ''}
                        </div>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('inscriptions.creationdate')}:</small> {data.creationDate ? `${new Date(data.creationDate).getDate() > 9 ? new Date(data.creationDate).getDate() : "0" + new Date(data.creationDate).getDate()}/${new Date(data.creationDate).getMonth() > 8 ? parseInt(new Date(data.creationDate).getMonth()) + 1 : "0" + (parseInt(new Date(data.creationDate).getMonth()) + 1)}/${new Date(data.creationDate).getFullYear() > 9 ? new Date(data.creationDate).getFullYear() : "0" + new Date(data.creationDate).getFullYear()}`: '-'}
                        </div>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('payments.member')}:</small>
                            {data.client ? `${data.client?.lastname}, ${data.client?.firstname} - ${data.client?.phone}` : '-'}
                        </div>
                        <div className='d-flex justify-content-between p-2'>
                            <small>{t('inscriptions.sepapaid')}</small> {data?.sepaPaid ? t('global.yes') : t('global.no')}
                        </div>
                    </Col>
                </Row>
            </Container>
        )
    }

    return (
        <Container fluid className='cuerpo px-2 px-xl-4'>
            <Row className='mt-3 mb-5'>
                <Col className='d-flex align-items-center'>
                    <h1 className='ms-2 my-0'>{t('club.payments')}</h1>
                </Col>
            </Row>
            <Row className='my-3'>
                <Col>
                    <Card>
                        <Card.Body>
                            <h2>{t('payments.paymentsList')}</h2>
                            <hr className='mb-4'/>
                            <div className='d-flex flex-wrap gap-2 mb-3'>
                                {availableYears.map((year) => (
                                    <button
                                        key={year}
                                        type='button'
                                        className={`btn btn-sm ${selectedTab === year ? 'btn-dark' : 'btn-outline-dark'}`}
                                        onClick={() => {
                                            setSelectedTab(year)
                                            setCurrentPage(1)
                                        }}
                                    >
                                        {year}
                                    </button>
                                ))}
                                <button
                                    type='button'
                                    className={`btn btn-sm ${selectedTab === 'all' ? 'btn-dark' : 'btn-outline-dark'}`}
                                    onClick={() => {
                                        setSelectedTab('all')
                                        setCurrentPage(1)
                                    }}
                                >
                                    Todos
                                </button>
                                <button
                                    type='button'
                                    className={`btn btn-sm ${selectedTab === 'inactive' ? 'btn-dark' : 'btn-outline-dark'}`}
                                    onClick={() => {
                                        setSelectedTab('inactive')
                                        setCurrentPage(1)
                                    }}
                                >
                                    Inactivos
                                </button>
                            </div>
                            <div className='d-flex justify-content-between align-items-end gap-2 mb-3 flex-wrap'>
                                <div style={{flex: 1}}></div>
                                <div className='d-flex flex-column align-items-center' style={{flex: 1}}>
                                    <h2 className='mb-1'>Buscador</h2>
                                    <div className='data-table-extensions-filter'>
                                        <span className='icon' />
                                        <input
                                            type='text'
                                            className='filter-text'
                                            placeholder={t('datatables.buscar')}
                                            value={searchInput}
                                            onChange={(e) => {
                                                setSearchInput(e.target.value)
                                                setCurrentPage(1)
                                            }}
                                        />
                                    </div>
                                </div>
                                <div style={{flex: 1}}></div>
                            </div>
                            <div className='my-3'>
                                <Row>
                                    <Col>
                                        {
                                            isInitialLoading
                                                ?
                                                    <Loader/>
                                                :
                                                    <>
                                                        {
                                                            tablePayments.length > 0 ?
                                                                <CustomDataTable
                                                                    key={`${selectedTab}-${rowsPerPage}`}
                                                                    columns={columnsPayments}
                                                                    data={tablePayments}
                                                                    exportable={false}
                                                                    printable={false}
                                                                    expandedData={ExpandedRow}
                                                                    paginationServer={true}
                                                                    paginationTotalRows={pagination?.total || 0}
                                                                    paginationPerPage={rowsPerPage}
                                                                    paginationDefaultPage={currentPage}
                                                                    onChangePage={(page) => setCurrentPage(page)}
                                                                    onChangeRowsPerPage={(newPerPage) => {
                                                                        const perPageNumber = Number(newPerPage)
                                                                        const totalRows = Number(pagination?.total || 0)
                                                                        const hasNumericPerPage = Number.isFinite(perPageNumber) && perPageNumber > 0
                                                                        const showAll =
                                                                            String(newPerPage).toLowerCase() === 'all' ||
                                                                            newPerPage === -1 ||
                                                                            !hasNumericPerPage ||
                                                                            (totalRows > 0 && hasNumericPerPage && perPageNumber === totalRows)
                                                                        setAllRowsSelected(showAll)
                                                                        setRowsPerPage(hasNumericPerPage ? perPageNumber : (totalRows > 0 ? totalRows : 25))
                                                                        setCurrentPage(1)
                                                                    }}
                                                                />
                                                            :
                                                                <div className='contenedor-vacio text-center'>
                                                                    <h4>{t('payments.noPayments')}</h4>
                                                                    {t('payments.thereIsNoPaymentsPhrase')}
                                                                </div>
                                                        }
                                                    </>
                                        }

                                    </Col>
                                </Row>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <ManualPayment userType="club" payment={manualPaymentSelected} onHide={() => setManualPaymentSelected(null)}/>
            <ReturnPayment userType="club" payment={paymentToReturnSelected} onHide={() => setPaymentToReturnSelected(null)}/>
        </Container>
    )
}
